<template>
  <div class="constructor_section__wrapper">
    <template
      v-for="(section, index) in componentsRender"
      :key="section.type + section.layout"
    >
      <SeoText v-if="!isHomePage && section.type === 'user_view_history'" />
      <component :is="section.type" :data="section" class="section" />
      <div v-if="isHomePage && index === 7">
        <CategorySliders />
      </div>
      <div v-if="isHomePage && index === 7" class="section blog-section">
        <div class="l-container">
          <BlogArticles
            :articles="blogArticlesData"
            type="main"
            class="content-block"
            full-width-mobile
          />
        </div>
      </div>
    </template>
    <div id="mainPageEnd"></div>
  </div>
</template>

<script lang="ts">
import type { Sections } from '~/interfaces/app';
import OfferList from '~/components/constructor-components/offer-list/OfferList.vue';
import CategorySliders from '~/components/category-sliders/CategorySliders.vue';
import { useGlobalStore } from '~/stores/global';
import { useInteractiveStore } from '~/stores/interactive';
import SeoText from '~/components/constructor-components/seo-text/SeoText.vue';
import { getArticles } from '~/api/global';
import { base64ToObj, objToBase64 } from '~/utils/base64';

const WebSlider = defineAsyncComponent(
  () => import('~/components/constructor-components/web-slider/WebSlider.vue')
);
const Links = defineAsyncComponent(
  () => import('~/components/constructor-components/links/Links.vue')
);
const cataloglist = defineAsyncComponent(
  () =>
    import('~/components/constructor-components/catalog-list/CatalogList.vue')
);
const UserViewHistory = defineAsyncComponent(
  () =>
    import(
      '~/components/constructor-components/user-view-history/UserViewHistory.vue'
    )
);
const BannerList = defineAsyncComponent(
  () => import('~/components/constructor-components/banner-list/BannerList.vue')
);
const Promocodes = defineAsyncComponent(
  () => import('~/components/constructor-components/promocodes/Promocodes.vue')
);
const Brands = defineAsyncComponent(
  () => import('~/components/constructor-components/brands/Brands.vue')
);
const ShopsPopular = defineAsyncComponent(
  () =>
    import('~/components/constructor-components/shop-popular/ShopsPopular.vue')
);
const CategoriesPopular = defineAsyncComponent(
  () =>
    import(
      '~/components/constructor-components/categories-popular/CategoriesPopular.vue'
    )
);
const BlogArticles = defineAsyncComponent(
  () => import('~/components/blog-articles/BlogArticles.vue')
);
const SlugList = defineAsyncComponent(
  () => import('~/components/constructor-components/slug-list/SlugList.vue')
);

export default defineComponent({
  name: 'ConstructorDistributor',
  components: {
    SeoText,
    'web-slider': WebSlider,
    Links,
    cataloglist,
    user_view_history: UserViewHistory,
    offerlist: OfferList,
    bannerlist: BannerList,
    promocodes: Promocodes,
    brands: Brands,
    'shops-popular': ShopsPopular,
    categories_popular: CategoriesPopular,
    BlogArticles,
    SlugList,
    CategorySliders,
  },
  props: {
    sections: {
      type: Array as () => Sections[],
      default: (): Sections[] => [],
    },
    isHomePage: {
      type: Boolean,
      default: true,
    },
  },
  async setup(props) {
    const globalStore = useGlobalStore();
    const interactiveStore = useInteractiveStore();
    const isFirstInteractive = computed(
      () => interactiveStore.isFirstInteractive
    );
    const isBot = computed(() => globalStore.isBot);
    const allSections = ref([...props.sections]);
    const showBlocksToOnce = 3;
    const componentsRender = ref(
      isBot.value
        ? allSections.value
        : allSections.value.slice(0, showBlocksToOnce)
    );
    const countBlock = ref(0);

    const componentShow = (countSlice: number) => {
      componentsRender.value = allSections.value.slice(0, countSlice);
      countBlock.value = countSlice;
      allSections.value.splice(0, countSlice);
    };
    onBeforeMount(() => {
      componentShow(isBot.value ? props.sections.length : showBlocksToOnce);
    });

    const addComponentsRender = () => {
      componentsRender.value = componentsRender.value.concat(
        allSections.value.slice(0, 1)
      );
      allSections.value.splice(0, 1);
      countBlock.value++;
    };

    if (!import.meta.env.SSR && window && !isBot.value) {
      setTimeout(() => {
        window.addEventListener('scroll', checkPosition);
        window.addEventListener('resize', checkPosition);
        window.addEventListener('touchmove', checkPosition, false);
      }, 1000);
    }

    const region_id = computed(() =>
      globalStore?.region?.id ? String(globalStore?.region?.id) : '1'
    );
    const blogArticlesData = ref<any>([]);
    onUnmounted(() => {
      if (!import.meta.env.SSR && window && !isBot.value) {
        window.removeEventListener('scroll', checkPosition);
        window.removeEventListener('resize', checkPosition);
        window.removeEventListener('touchmove', checkPosition, false);
      }
    });
    const resData = await useAsyncData('dataBlogs', async () => {
      const data = await getArticles(null).then((res) => res);
      return objToBase64({ data });
    });
    if (resData.data.value) {
      const data = base64ToObj(resData.data.value).data;
      if (data) {
        blogArticlesData.value = data;
      }
    }

    const checkPosition = () => {
      const height = document.body.offsetHeight;
      const screenHeight = window.innerHeight;
      const scrolled = window.scrollY;
      const threshold = height - screenHeight / 4;
      const position = scrolled + screenHeight;
      if (position + 600 >= threshold) {
        addComponentsRender();
      }
    };

    return {
      region_id,
      isFirstInteractive,
      blogArticlesData,
      componentsRender,
      countBlock,
      allSections,
    };
  },
});
</script>

<style lang="scss" scoped>
.constructor_section__wrapper {
  .section:last-child {
    margin-bottom: 0;
  }

  .section {
    &.history,
    &.blog-section {
      margin: 0 auto 48px;
      @include lg-block {
        margin: 0 auto 120px;
      }

      :deep(h2.header) {
        margin-bottom: 12px;
        @include md-block {
          margin-bottom: 16px;
        }
        @include lg-block {
          margin-bottom: 20px;
        }
        @include xxl-block {
          margin-bottom: 28px;
          font-size: 32px;
          line-height: 32px;
        }
      }
    }
  }

  :deep(.category-block) {
    margin: 0 auto 48px;
    @include lg-block {
      margin: 0 auto 120px;
    }
  }
}
</style>
